// Scripted Q&A for the chat-demo mode of the analysis assistant (see
// demoFlags). With the flag on, each click of the send button plays the
// next exchange (the "user" question appears, then the answer streams in)
// without calling the AI backend. The content walks the AWS Private Pricing
// Addendum analysis (opportunity, ramp, Savings Plans, credits, must-haves,
// leverage), and is deliberately deal-agnostic enough to run on any project.

export interface AssistantDemoExchange {
    question: string;
    /** Markdown; revealed progressively to look like a streamed reply. */
    answer: string;
}

export const ASSISTANT_DEMO_EXCHANGES: AssistantDemoExchange[] = [
    {
        question: "What's the single biggest opportunity in this deal?",
        answer:
            "The **year-over-year commitment ramp**. The addendum ramps $50M to $58M to $65M, " +
            "about **14% YoY**, while typical large-enterprise PPAs assume **8-10%** unless " +
            "tied to a discrete migration. Normalising the ramp to 8% is worth up to " +
            "**$10.68M over the term**: the largest of the three levers, and the easiest to " +
            "defend because it's a straight market-norm argument.\n\n" +
            "All three levers together target **$13.4M-$22.5M** (7.7%-13.0% of TCV), with a " +
            "target of **$17.9M (10.4%)**.",
    },
    {
        question: "Why is the 14% ramp a problem if our usage is growing anyway?",
        answer:
            "Three reasons:\n\n" +
            "1. **It's an escalator in disguise.** AWS avoids raising unit prices; a steep " +
            "ramp achieves the same over-recovery through minimum commitments instead.\n" +
            "2. **FSI demand growth is structurally constrained.** Regulatory approvals, " +
            "security gating, and long planning cycles make sustained double-digit growth " +
            "hard to hit. The shortfall penalties are carried forward, with no " +
            "flex-down right.\n" +
            "3. **AWS still wins at 8-10%.** They lock in expanding revenue on a strategic " +
            "account, and a realistic ramp avoids the wasteful \"burn spend\" behaviour that " +
            "misaligned commitments create late in the term.",
    },
    {
        question: "How do our Savings Plan discounts compare to market?",
        answer:
            "Below the top quartile at your spend tier:\n\n" +
            "| Area | This deal | Market at $150M+ |\n" +
            "| --- | --- | --- |\n" +
            "| EC2 Instance Savings Plans | 25% | 25-40% |\n" +
            "| Compute Savings Plans | 22% | 25-45% |\n" +
            "| Cross-Service Discount | 19% | 15-25% |\n\n" +
            "The cross-service discount is fine; the Savings Plans are the gap. They're " +
            "margin-favourable for AWS (they lock in multi-year predictable demand), which " +
            "is exactly the argument for pricing them at the top of the band. A 5-10 point " +
            "uplift on 45% of spend is worth **$3.9M-$7.8M** over the term.",
    },
    {
        question: "What about the $7M of credits, aren't those real value?",
        answer:
            "Less than they look. The $7M splits into **$4M of AI/ML credits** " +
            "(milestone-based) and **$3M of migration credits** (attestation-based), all " +
            "**conditional, expiring, and behaviour-driven**. At ~4% of TCV, credit reliance " +
            "is above the <2-3% buyer-optimised norm.\n\n" +
            "Credits obscure the effective rate, are hard to forecast against, and often " +
            "expire unused: value that flows straight back to AWS. The play is to convert " +
            "**$2M-$4M into unconditional baseline rate reductions** on the Cross-Service or " +
            "Savings Plan discount. You're already committing the usage the credits are " +
            "supposed to incentivise, so the conversion costs AWS little and buys you " +
            "transparency.",
    },
    {
        question: "What must we get into the contract before signing?",
        answer:
            "Six must-have amendments, bundled as one structural package:\n\n" +
            "1. **Flex-down rights** on the annual commitment, with no shortfall penalties " +
            "when actual spend runs lower.\n" +
            "2. **Marketplace cap raised from 25% to 50%** of commitment.\n" +
            "3. **Tiered egress pricing** with volume caps on cross-region transfer.\n" +
            "4. **Credit-to-discount conversion** (up to $4M, unconditional).\n" +
            "5. **Termination for convenience restored** during the PPA (180 days' notice).\n" +
            "6. **Unconditional discount applicability**: future services inherit the " +
            "negotiated discounts, and the Ineligible Services list is frozen.\n\n" +
            "Frame them as enterprise governance prerequisites for a $173M, 3-year FSI " +
            "commitment, not as concessions.",
    },
    {
        question: "Do we actually have leverage against AWS here?",
        answer:
            "Yes: **3.6 / 5.0 composite**, if it's actively exercised.\n\n" +
            "- **Scale (5/5):** $173M with a 14% ramp puts you in the strategic tier where " +
            "pricing is negotiated, not formulaic.\n" +
            "- **Alternatives (4/5):** Azure and GCP are credible for FSI workloads if the " +
            "consideration is signalled at workload level, not as soft benchmarking.\n" +
            "- **Timing (4/5):** AWS sales is quota-driven; hold the final moves for their " +
            "quarter and year-end window.\n" +
            "- **Embeddedness (1/5):** wholesale exit isn't credible, so don't threaten it.\n\n" +
            "The playbook: aim the leverage at **commercial structure** (ramp, Savings Plan " +
            "depth, egress, marketplace, credits) and make the final commitment visibly " +
            "conditional on all six must-haves landing in the redline.",
    },
];
